import type {
  AdventureSceneDefinition,
  ConditionDefinition,
  HotspotDefinition,
} from '../../domain/types';

export const h01Symbols = [
  {
    id: 'scale',
    name: 'Vægten',
    trade: 'Handel',
    x: 700,
    explanation: 'Vægten vejer købmandens varer. Den passer til handel.',
  },
  {
    id: 'saw',
    name: 'Saven',
    trade: 'Håndværk',
    x: 895,
    explanation: 'Saven former træ i håndværkerens hænder. Den passer til håndværk.',
  },
  {
    id: 'sheaf',
    name: 'Kornneget',
    trade: 'Landbrug',
    x: 1090,
    explanation: 'Kornet høstes på marken. Kornneget passer til landbrug.',
  },
  {
    id: 'gear',
    name: 'Tandhjulet',
    trade: 'Industri',
    x: 1285,
    explanation: 'Tandhjulet driver maskinerne. Det passer til industri.',
  },
] as const;

export const h01Matched: ConditionDefinition[] = h01Symbols.map((symbol) => ({
  type: 'flag',
  key: 'h01:' + symbol.id,
  equals: true,
}));
export const h01NotCollected: ConditionDefinition = {
  type: 'not',
  condition: { type: 'flag', key: 'h01:sackCollected', equals: true },
};

const symbolHotspots: HotspotDefinition[] = h01Symbols.map((symbol) => ({
  id: 'h01-' + symbol.id,
  label: symbol.name,
  shape: { type: 'rect', x: symbol.x - 78, y: 672, width: 156, height: 218 },
  debugColor: '#e7b94d',
  actions: [
    {
      id: 'look-' + symbol.id,
      verb: 'look',
      label: 'Se på',
      effects: [{ type: 'message', text: symbol.explanation }],
    },
    ...h01Symbols.map((target) => ({
      id: 'match-' + symbol.id + '-' + target.id,
      verb: 'use' as const,
      label: 'Læg ved ' + target.trade.toLocaleLowerCase('da'),
      conditions: [
        {
          type: 'not' as const,
          condition: { type: 'flag' as const, key: 'h01:' + symbol.id, equals: true },
        },
      ],
      effects:
        symbol.id === target.id
          ? [
              { type: 'setFlag' as const, key: 'h01:' + symbol.id, value: true },
              { type: 'playSound' as const, soundId: 'symbol-matched' },
              { type: 'message' as const, text: symbol.explanation },
            ]
          : [{ type: 'message' as const, text: 'Prøv igen. ' + symbol.explanation }],
    })),
  ],
}));

export const raadhustorvetScene: AdventureSceneDefinition = {
  id: 'raadhustorvet-scene',
  title: 'Rådhustorvet · Fire erhverv',
  historicalPeriod: 'Udflugtsbogens fiktive torvedag',
  backgroundAssetId: 'raadhustorvet-background',
  exitLocationId: 'raadhustorvet',
  layers: [],
  documentedHistory: [
    'To figurgrupper af Sigurjón Ólafsson står foran Vejle Rådhus. De blev afsløret i 1955.',
    'Erhvervene er vist med en vægt for handel, en sav for håndværk, et kornneg for landbrug og et tandhjul for industri.',
  ],
  gameplayFiction: [
    'Torveværten, købmandsboden og forberedelserne til fjordballet er opdigtede.',
    'Symbolkortene er nye spilrekvisitter. Illustrationen er en fri fortolkning af torvet.',
    'Kornsækken og leverancen til Den Smidtske Gård er en del af spillets genstandskæde.',
  ],
  fictionLabel: 'Fiktiv torvedag i udflugtsbogen.',
  sourceReferences: [
    {
      title: 'Figurgrupper v. Rådhuset',
      publisher: 'Vejle Kunstmuseum',
      url: 'https://www.vejlekunstmuseum.dk/skulpturguide/skulpturer/figurgrupper-raadhuset/',
    },
    {
      title: 'Sigurjón Ólafssons skulpturer på Rådhustorvet',
      publisher: 'VejleWiki',
      url: 'https://www.vejlewiki.dk/index.php?title=Sigurj%C3%B2n_Olafss%C3%B2ns_skulpturer_p%C3%A5_R%C3%A5dhustorvet',
    },
  ],
  hotspots: [
    {
      id: 'h01-host',
      label: 'Torveværten',
      shape: { type: 'rect', x: 300, y: 360, width: 240, height: 510 },
      actions: [
        {
          id: 'look-host',
          verb: 'look',
          label: 'Se på',
          effects: [
            {
              type: 'message',
              text: 'Værten har dækket et bord med fire symbolkort. En udflugtsbog ligger åben ved siden af.',
            },
          ],
        },
        {
          id: 'talk-host',
          verb: 'talk',
          label: 'Tal med',
          conditions: [h01NotCollected],
          effects: [{ type: 'startDialogue', dialogueId: 'h01-host' }],
        },
        {
          id: 'talk-host-complete',
          verb: 'talk',
          label: 'Tal med',
          conditions: [{ type: 'flag', key: 'h01:sackCollected', equals: true }],
          effects: [{ type: 'startDialogue', dialogueId: 'h01-complete' }],
        },
      ],
    },
    {
      id: 'h01-sculptures',
      label: 'Figurgrupperne',
      shape: { type: 'rect', x: 690, y: 350, width: 610, height: 278 },
      actions: [
        {
          id: 'look-sculptures',
          verb: 'look',
          label: 'Se på',
          effects: [
            {
              type: 'message',
              text: 'Figurgrupperne fortæller om handel, håndværk, landbrug og industri. På værtens bord ligger fire kort med de samme erhvervssymboler.',
            },
          ],
        },
      ],
    },
    ...symbolHotspots,
    {
      id: 'h01-sack',
      label: 'Den tomme kornsæk',
      shape: { type: 'rect', x: 1500, y: 645, width: 270, height: 310 },
      visibleConditions: [h01NotCollected],
      actions: [
        {
          id: 'look-grain-sack',
          verb: 'look',
          label: 'Se på',
          effects: [
            {
              type: 'message',
              text: 'En tom sæk af groft stof. På mærket står: Den Smidtske Gård. Først skal de fire symbolkort ligge ved de rigtige erhverv.',
            },
          ],
        },
        {
          id: 'take-grain-sack',
          verb: 'take',
          label: 'Tag',
          conditions: [...h01Matched, h01NotCollected],
          unavailableMessage:
            'Værten siger: “Find først de fire erhverv på bordet. Så er sækken din.”',
          effects: [
            {
              type: 'addItem',
              itemId: 'emptyGrainSack',
              message: 'Den tomme kornsæk er lagt i rygsækken.',
            },
            { type: 'setFlag', key: 'h01:sackCollected', value: true },
            { type: 'markPuzzleSolved', puzzleId: 'h01-four-trades' },
            { type: 'playSound', soundId: 'item-acquired' },
            { type: 'startDialogue', dialogueId: 'h01-complete' },
          ],
        },
      ],
    },
    {
      id: 'h01-book',
      label: 'Udflugtsbogen',
      shape: { type: 'rect', x: 460, y: 680, width: 155, height: 130 },
      actions: [
        {
          id: 'read-excursion-book',
          verb: 'look',
          label: 'Læs',
          conditions: [h01NotCollected],
          effects: [{ type: 'startDialogue', dialogueId: 'h01-host' }],
        },
        {
          id: 'read-excursion-book-complete',
          verb: 'look',
          label: 'Læs',
          conditions: [{ type: 'flag', key: 'h01:sackCollected', equals: true }],
          effects: [
            { type: 'message', text: 'Første opgave er klaret. Den tomme kornsæk er nu din.' },
          ],
        },
      ],
    },
    {
      id: 'h01-exit',
      label: 'Vejen tilbage til kortet',
      shape: { type: 'rect', x: 35, y: 885, width: 300, height: 150 },
      actions: [
        {
          id: 'h01-exit-map',
          verb: 'exit',
          label: 'Tilbage til kortet',
          effects: [{ type: 'returnToMap' }],
        },
      ],
    },
  ],
};
